<template>
    <div class="s2-textarea-container" :class="{ 'listening-active': isListening }" ref="textareaContainer">
        <a-textarea v-model="cloneValue" v-bind="filteredAttrs" v-on="filteredListeners" ref="textarea" />
        <AICopilot
            v-if="aiCopilot"
            @close="aiCopilot = null"
            @change="updateTextFromAI"
            :payload="aiCopilot"
            :type="'message'"
        />
        <div class="action-buttons">
            <div class="ai-button" @click="openAICopilot" v-if="showAITool">
                <img src="/static/img/copilot.png" width="20" height="20" />
            </div>
            <div
                class="mic-button"
                :class="{ active: isListening }"
                @click="()=>isListening ? stopListening('mic-button') : startListening()"
            >
                <a-icon type="audio" theme="filled" />
                <span v-if="isListening">{{ $t('message.speakNow') }}</span>
            </div>
        </div>
        <div class="voice-shortcut-hint" v-if="showShortcutHint || isListening">
            <template v-if="isListening">
                <div class="listening-waves">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <div class="listening-text">
                    <span>{{ $t('message.listeningInProgress') }}</span>
                    <small>{{ $t('message.spaceKeyReleaseToSave') }}</small>
                </div>
            </template>
            <template v-else>
                <a-icon type="keyboard" />
                <span>{{ $t('message.spaceKeyHint') }}</span>
            </template>
        </div>
        <p v-if="speechSupported === false" class="speech-not-supported">
            {{ $t('message.browserSpeechRecognitionNotSupported') }}
        </p>
    </div>
</template>

<script>
import AICopilot from "./popup-chat/chat-window/ChatMessageArea/components/AICopilot.vue";

export default {
    name: "S2Textarea",
    inheritAttrs: false,
    components: { AICopilot },
    props: {
        value: {
            type: String,
            default: "",
        },
        showShortcutHint: {
            type: Boolean,
            default: true,
        },
        focusOnMount: {
            type: Boolean,
            default: false,
        },
        showAITool: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            recognition: null,
            isListening: false,
            speechSupported: true,
            originalText: "",
            cloneValue: "",
            aiCopilot: null,
        };
    },
    computed: {
        filteredAttrs() {
            const attrs = { ...this.$attrs };
            delete attrs.value;
            return attrs;
        },
        filteredListeners() {
            const listeners = { ...this.$listeners };
            delete listeners.input;
            return listeners;
        },
    },
    mounted() {
        this.initSpeechRecognition();
        window.addEventListener("keydown", this.handleKeyDown);
        window.addEventListener("keyup", this.handleKeyUp);
        if (this.focusOnMount) {
            this.$nextTick(() => {
                if (this.$refs.textarea) {
                    this.$refs.textarea.focus();
                }
            });
        }
    },
    created() {
        console.log("[created]", this.$el);
        this.cloneValue = this.value;
    },
    beforeDestroy() {
        window.removeEventListener("keydown", this.handleKeyDown);
        window.removeEventListener("keyup", this.handleKeyUp);
        if (this.recognition) {
            try {
                this.recognition.stop();
            } catch (error) {
                console.error("Stop recognition error on destroy:", error);
            }
        }
    },
    methods: {
        initSpeechRecognition() {
            if (this.recognition) {
                console.log("[initSpeechRecognition] recognition already initialized");
                return;
            }
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (!SpeechRecognition) {
                this.speechSupported = false;
                return;
            }

            this.recognition = new SpeechRecognition();
            this.recognition.lang = "tr-TR";
            this.recognition.continuous = true;
            this.recognition.interimResults = true;

            this.recognition.onstart = () => {
                this.isListening = true;
                this.originalText = this.value;
            };

            this.recognition.onresult = (event) => {
                // Tüm sonuçları al
                const results = Array.from(event.results);
                let finalTranscript = "";
                let interimTranscript = "";

                // Tüm sonuçları işle
                for (let i = 0; i < results.length; i++) {
                    const result = results[i];
                    const transcript = result[0].transcript;

                    if (result.isFinal) {
                        finalTranscript += transcript + " ";
                        this.recognition.stop();
                    } else {
                        interimTranscript += transcript + " ";
                    }
                }

                // Hem final hem de geçici sonuçları birleştir
                const fullText =
                    this.originalText + (this.originalText ? " " : "") + finalTranscript + interimTranscript;

                this.isListening = false;
                this.cloneValue = fullText;
                this.$emit("input", fullText);
            };

            this.recognition.onend = () => {
                this.isListening = false;
                this.recognition.stop();
            };

            this.recognition.onerror = (event) => {
                console.error("Speech recognition error", event.error);
                this.isListening = false;
                this.recognition.stop();
            };
        },

        startListening() {
            if (this.isListening) {
                console.log("[startListening] recognition is already active, skipping...");
                return;
            }

            console.log("[startListening]");
            if (!this.recognition) {
                this.initSpeechRecognition();
                if (!this.speechSupported) return;
            }

            try {
                this.isListening = true;
                this.$forceUpdate(); // Görüntüyü zorla güncelle
                this.recognition.start();
            } catch (error) {
                console.error("Start recognition error:", error);
                this.isListening = false;
                this.$forceUpdate(); // Hata durumunda da görüntüyü güncelle
            }
        },

        stopListening(type) {
            console.log("[stopListening]", type);

            if (this.recognition) {
                try {
                    this.recognition.stop();
                    this.isListening = false;
                    this.$forceUpdate(); // Görüntüyü zorla güncelle
                } catch (error) {
                    console.error("Stop recognition error:", error);
                }
            }
        },
        handleKeyDown(event) {
            const isInputFocused = document.activeElement === this.$refs.textarea;
            const isTextArea = event.target.tagName === "TEXTAREA";
            if (isInputFocused || isTextArea) return;

            // Space key (code 32)
            if (event.code === "Space") {
                event.preventDefault();
                event.stopPropagation();
                if (!event.repeat && !this.isListening) {
                    this.startListening();
                }
            }
        },
        handleKeyUp(event) {
            // Space key release
            if (event.code === "Space" && this.isListening) {
                event.preventDefault();
                event.stopPropagation();
                console.log("[handleKeyUp] stopListening calling from handleKeyUp");
                this.stopListening("handleKeyUp");
            }
        },
        openAICopilot() {
            // Textarea'nın pozisyonunu ve değerini alıp AI Copilot'a gönderiyoruz
            const textarea = this.$refs.textarea;
            if (!textarea) return;

            const position = {
                start: textarea.selectionStart || 0,
                end: textarea.selectionEnd || 0,
            };

            this.aiCopilot = {
                message: this.cloneValue,
                position: position,
            };
        },
        updateTextFromAI(text) {
            this.cloneValue = text;
            this.$emit("input", text);
        },
    },
    watch: {
        value(newVal) {
            this.cloneValue = newVal;
        },
        cloneValue(newVal) {
            const isChanged = this.cloneValue !== this.value;

            if (isChanged && !this.isListening) {
                this.$emit("input", newVal);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.s2-textarea-container {
    position: relative;
    transition: all 0.3s ease;
    width: 100%;

    &.listening-active {
        box-shadow: 0 0 0 2px #1890ff, 0 0 20px rgba(24, 144, 255, 0.5);
        border-radius: 4px;

        ::v-deep .ant-input {
            border-color: #1890ff;
        }
    }

    .action-buttons {
        position: absolute;
        bottom: 33px;
        right: 4px;
        display: flex;
        align-items: center;
        gap: 8px;
        z-index: 2;
    }

    .ai-button {
        background-color: #f0f2f5;
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
            background-color: #e6f7ff;
            color: #1890ff;
        }

        i {
            font-size: 18px;
        }
    }

    .mic-button {
        position: relative;
        background-color: #f0f2f5;
        border-radius: 50%;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
            background-color: #e6f7ff;
        }

        &.active {
            background-color: #1890ff;
            color: white;
            width: auto;
            border-radius: 18px;
            padding: 0 12px;

            span {
                margin-left: 8px;
            }
        }

        i {
            font-size: 18px;
        }
    }

    .voice-shortcut-hint {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 10px;
        color: #8c8c8c;
        font-size: 12px;

        .anticon {
            margin-right: 5px;
        }

        .listening-waves {
            display: flex;
            align-items: center;
            height: 20px;
            margin-right: 10px;

            span {
                display: block;
                width: 3px;
                margin: 0 2px;
                background-color: #1890ff;
                animation: wave 1.2s infinite ease-in-out;

                &:nth-child(1) {
                    height: 8px;
                    animation-delay: 0s;
                }
                &:nth-child(2) {
                    height: 12px;
                    animation-delay: 0.3s;
                }
                &:nth-child(3) {
                    height: 16px;
                    animation-delay: 0.6s;
                }
                &:nth-child(4) {
                    height: 12px;
                    animation-delay: 0.9s;
                }
            }
        }

        .listening-text {
            display: flex;
            flex-direction: column;

            span {
                font-weight: bold;
                color: #1890ff;
            }

            small {
                margin-top: 3px;
                font-size: 11px;
                color: #8c8c8c;
            }
        }
    }

    .speech-not-supported {
        color: #ff4d4f;
        font-size: 12px;
        margin-top: 8px;
    }
}

@keyframes wave {
    0%,
    100% {
        transform: scaleY(0.5);
    }
    50% {
        transform: scaleY(1);
    }
}
</style>
